import React, {
  Component, lazy, Suspense, Fragment,
} from 'react';
import PropTypes from 'prop-types';
import ProjectSummary from '../ProjectSummary';
import ProjectSidebar from './ProjectSidebar';
import { getRequest } from '../../requestUtils';
import Loading from '../common/Loading';
import './ProjectNav.scss';
import { analyticsTrack } from '../../analyticsUtils';

// lazy lets you defer loading component’s code until it is rendered for the first time.
const ProjectProposalsTab = lazy(() => import('../ProjectProposalsTab'));
const AlgoliaInstantSearch = lazy(() => import('../FeaturedProfiles/AlgoliaInstantSearch'));
const ScreeningQuestions = lazy(() => import('./ScreeningQuestions'));
const ConsultantComparison = lazy(() => import('./ConsultantComparison'));

const tabs = (showScreeningQuestions, showConsultantComparisonTab) => {
  const tabsArray = [
    { key: 'proposals', label: 'Proposals' },
    { key: 'recommended', label: 'Recommended Consultants' },
    { key: 'details', label: 'Project Details' },
  ];
  if (showConsultantComparisonTab) tabsArray.push({ key: 'comparison', label: 'Consultant Comparison' });
  if (showScreeningQuestions) tabsArray.push({ key: 'screening', label: 'Screening Questions' });
  return tabsArray;
};

class ProjectNav extends Component {
  constructor(props) {
    super(props);
    const { showScreeningQuestions, showConsultantComparisonTab } = props;
    const tabList = tabs(showScreeningQuestions, showConsultantComparisonTab);

    // Get tab from URL or default to 'details'
    const urlParams = new URLSearchParams(window.location.search);
    const indexParam = urlParams.get('tab');
    const defaultTab = tabList.find((tab) => tab.key === indexParam) ? indexParam : 'details';

    this.state = {
      selectedTab: defaultTab,
      project: null,
      nextStepsData: props.nextStepsData,
    };
    this.clickTab = this.clickTab.bind(this);
    this.renderTab = this.renderTab.bind(this);
    this.renderContent = this.renderContent.bind(this);
    this.updateNextSteps = this.updateNextSteps.bind(this);
  }

  componentDidMount() {
    this.fetchProjectData();
  }

  fetchProjectData() {
    const { projectResourceId } = this.props;

    getRequest(`/projects/${projectResourceId}`, { form_step: 'review' }).then((response) => {
      this.setState({
        project: response.data,
      });
    });
  }

  clickTab(tabKey) {
    const { selectedTab } = this.state;
    const url = new URL(window.location);

    // Only clear consultants if you are LEAVING the comparison tab
    if (selectedTab === 'comparison' && tabKey !== 'comparison') {
      url.searchParams.delete('consultants_Ids');
    }

    url.searchParams.set('tab', tabKey);
    window.history.pushState({}, '', url);

    this.setState({ selectedTab: tabKey });
  }

  projectFilters() {
    const { project } = this.state;
    if (!project) return {};

    return {
      refinementList: {
        'area_of_expertises.name': project.area_of_expertises.map((aoe) => aoe.name),
        'therapeutic_areas.name': project.therapeutic_areas.map((ta) => ta.name).filter(
          (name) => project.priority_expertise_tags.includes(name),
        ),
        'therapies.name': project.therapies.map((th) => th.name).filter(
          (name) => project.priority_expertise_tags.includes(name),
        ),
        'higher_level_roles.name': project.higher_level_roles.map((hlr) => hlr.name).filter(
          (name) => project.priority_expertise_tags.includes(name),
        ),
        development_phases: project.development_phases.map((ph) => ph.name).filter(
          (name) => project.priority_expertise_tags.includes(name),
        ),
        seniority_levels: project.seniority.filter(
          (name) => project.priority_expertise_tags.includes(name),
        ),
      },
    };
  }

  updateNextSteps(nextStepsData = null) {
    if (nextStepsData !== null) {
      this.setState({ nextStepsData });
    } else {
      const { projectResourceId } = this.props;
      getRequest(`/projects/${projectResourceId}/next_steps`).then(({ data }) => {
        this.setState({ nextStepsData: data });
      });
    }
  }

  renderTab(tab, tabIndex) {
    const { selectedTab } = this.state;
    const { projectStatus } = this.props;
    const isSelected = selectedTab === tab.key;

    const isRecommendedTabHidden = tab.key === 'recommended'
      && !['employer_draft', 'clora_matching', 'employer_requested_matching'].includes(projectStatus);

    const hideTab = isRecommendedTabHidden ? 'd-none' : '';

    return (
      <a
        role="button"
        className={`body-text-2 medium-weight tab-active-${isSelected} ${hideTab}`}
        onClick={() => {
          this.clickTab(tab.key);
          if (tab.key === 'recommended') { analyticsTrack('Viewed recommended consultants tab'); }
        }}
        key={tab.key}
        tabIndex={tabIndex}
      >
        {tab.label}
      </a>
    );
  }

  renderContent() {
    const {
      projectResourceId, csrfToken, ownerOrCloraRep, projectId, cloraRep,
      showPublishedModal, userType, projectPermissionLevel, initialTeamMembers,
      invitableRoles, editableRoles, basePermissionsUrl, invitedColleagues,
      coworkersModalIsOpen, reps, shareOptions, shareRecipientOptions,
      salesBookingLink, invitationsCount, invitedFeedback, colleagues,
      currentUserEmail, permissions, cloraEmployer, indexName, agAppID,
      publicKey, userData, showProjectEmploymentType, inviterId, projectsOptions,
      searchUrl, isCollaborator, cloraRepName, consultants,
    } = this.props;
    const { project, selectedTab } = this.state;

    switch (selectedTab) {
      case 'proposals':
        return (
          <Suspense fallback={<Loading />}>
            <ProjectProposalsTab
              cloraRep={cloraRep}
              projectId={projectId}
              projectResourceId={projectResourceId}
              csrfToken={csrfToken}
              projectsOptions={projectsOptions}
              searchUrl={searchUrl}
              isCollaborator={isCollaborator}
              cloraRepName={cloraRepName}
            />
          </Suspense>
        );
      case 'recommended':
        return (
          <Suspense fallback={<Loading />}>
            <AlgoliaInstantSearch
              indexName={indexName}
              agAppID={agAppID}
              publicKey={publicKey}
              handleRegister={this.handleUserSignUp}
              csrfToken={csrfToken}
              userData={userData}
              getEmailStatus={() => userData.email}
              cloraEmployer={cloraEmployer}
              initialFilters={this.projectFilters()}
              projectResourceId={projectResourceId}
              location={selectedTab}
              inviterId={inviterId}
            />
          </Suspense>
        );
      case 'screening':
        return (
          <Suspense fallback={<Loading />}>
            <ScreeningQuestions
              csrfToken={csrfToken}
              projectId={projectId}
              updateNextSteps={this.updateNextSteps}
            />
          </Suspense>
        );
      case 'comparison':
        return (
          <Suspense fallback={<Loading />}>
            <ConsultantComparison
              consultants={consultants || []}
              projectResourceId={projectResourceId}
            />
          </Suspense>
        );
      default:
        return (
          <ProjectSummary
            project={project}
            projectResourceId={projectResourceId}
            csrfToken={csrfToken}
            permissions={permissions}
            projectPermissionLevel={projectPermissionLevel}
            ownerOrCloraRep={ownerOrCloraRep}
            showPublishedModal={showPublishedModal}
            userType={userType}
            initialTeamMembers={initialTeamMembers}
            invitableRoles={invitableRoles}
            editableRoles={editableRoles}
            basePermissionsUrl={basePermissionsUrl}
            coworkersModalIsOpen={coworkersModalIsOpen}
            reps={reps}
            shareOptions={shareOptions}
            shareRecipientOptions={shareRecipientOptions}
            salesBookingLink={salesBookingLink}
            invitationsCount={invitationsCount}
            invitedColleages={invitedColleagues}
            invitedFeedback={invitedFeedback}
            colleagues={colleagues}
            currentUserEmail={currentUserEmail}
            showProjectEmploymentType={showProjectEmploymentType}
            updateNextSteps={this.updateNextSteps}
            isCollaborator={isCollaborator}
          />
        );
    }
  }

  render() {
    const {
      userType, projectPermissionLevel, csrfToken, initialTeamMembers, editableRoles,
      basePermissionsUrl, coworkersModalIsOpen, reps, shareOptions, shareRecipientOptions,
      salesBookingLink, invitationsCount, invitedColleagues, colleagues, currentUserEmail,
      projectResourceId, invitableRoles, acceptedProposal, review, authUserCanSeeProposal,
      showHireAgain, showHourlyRate, showScreeningQuestions, googleOmniauthUrl,
      msOmniauthUrl, signInUrl, inviterId, showConsultantComparisonTab,
    } = this.props;
    const { selectedTab, project, nextStepsData } = this.state;

    return (
      <Fragment>
        <div className="container-fluid bg-white project-tabs-container px-0">
          <div className="d-flex flex-column flex-sm-row project-tabs pb-0">
            {tabs(showScreeningQuestions, showConsultantComparisonTab).map((tab, i) => this.renderTab(tab, i))}
          </div>

        </div>
        <div
          id={projectResourceId}
          className="g-row project-content project-container style-guide mt-4"
        >
          {/* LEFT SIDE */}
          {this.renderContent()}
          {/* RIGHT SIDE */}
          { !['Recommended Consultants', 'Consultant Comparison'].includes(selectedTab) && project
            ? (
              <ProjectSidebar
                project={project}
                userType={userType}
                projectPermissionLevel={projectPermissionLevel}
                csrfToken={csrfToken}
                initialTeamMembers={initialTeamMembers}
                invitableRoles={invitableRoles}
                editableRoles={editableRoles}
                basePermissionsUrl={basePermissionsUrl}
                coworkersModalIsOpen={coworkersModalIsOpen}
                reps={reps}
                shareOptions={shareOptions}
                shareRecipientOptions={shareRecipientOptions}
                salesBookingLink={salesBookingLink}
                invitationsCount={invitationsCount}
                invitedColleagues={invitedColleagues}
                colleagues={colleagues}
                currentUserEmail={currentUserEmail}
                acceptedProposal={acceptedProposal}
                review={review}
                authUserCanSeeProposal={authUserCanSeeProposal}
                showHireAgain={showHireAgain}
                showHourlyRate={showHourlyRate}
                nextStepsData={nextStepsData}
                clickTab={this.clickTab}
                projectResourceId={projectResourceId}
                googleOmniauthUrl={googleOmniauthUrl}
                msOmniauthUrl={msOmniauthUrl}
                signInUrl={signInUrl}
                inviterId={inviterId}
              />
            ) : ''}
        </div>
      </Fragment>
    );
  }
}

ProjectNav.propTypes = {
  cloraRep: PropTypes.bool.isRequired,
  projectResourceId: PropTypes.string.isRequired,
  csrfToken: PropTypes.string.isRequired,
  projectStatus: PropTypes.string.isRequired,
  projectId: PropTypes.number.isRequired,
  permissions: PropTypes.shape({}),
  ownerOrCloraRep: PropTypes.bool,
  showPublishedModal: PropTypes.bool,
  userType: PropTypes.string.isRequired,
  projectPermissionLevel: PropTypes.string,
  initialTeamMembers: PropTypes.shape({}),
  invitableRoles: PropTypes.arrayOf(PropTypes.string),
  editableRoles: PropTypes.arrayOf(PropTypes.string),
  basePermissionsUrl: PropTypes.string.isRequired,
  coworkersModalIsOpen: PropTypes.bool,
  reps: PropTypes.arrayOf(PropTypes.shape({})),
  shareOptions: PropTypes.arrayOf(PropTypes.shape({})),
  shareRecipientOptions: PropTypes.arrayOf(PropTypes.shape({})),
  salesBookingLink: PropTypes.string,
  invitationsCount: PropTypes.number,
  invitedColleagues: PropTypes.arrayOf(PropTypes.shape({})),
  invitedFeedback: PropTypes.shape({}),
  colleagues: PropTypes.arrayOf(PropTypes.shape({})),
  currentUserEmail: PropTypes.string,
  authUserCanSeeProposal: PropTypes.bool,
  acceptedProposal: PropTypes.shape({}),
  review: PropTypes.shape({}),
  showHourlyRate: PropTypes.bool,
  showHireAgain: PropTypes.bool,
  indexName: PropTypes.string.isRequired,
  agAppID: PropTypes.string.isRequired,
  publicKey: PropTypes.string.isRequired,
  cloraEmployer: PropTypes.bool,
  userData: PropTypes.shape({}),
  nextStepsData: PropTypes.shape({}),
  showProjectEmploymentType: PropTypes.bool,
  showScreeningQuestions: PropTypes.bool,
  signInUrl: PropTypes.string.isRequired,
  googleOmniauthUrl: PropTypes.string,
  msOmniauthUrl: PropTypes.string,
  inviterId: PropTypes.string,
  searchUrl: PropTypes.string,
  cloraRepName: PropTypes.string,
  projectsOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  tabSelected: PropTypes.string,
};

ProjectNav.defaultProps = {
  permissions: {},
  projectPermissionLevel: null,
  ownerOrCloraRep: false,
  showPublishedModal: false,
  reps: [],
  shareOptions: [],
  shareRecipientOptions: [],
  salesBookingLink: null,
  invitationsCount: 0,
  invitedColleagues: [],
  invitedFeedback: null,
  currentUserEmail: null,
  initialTeamMembers: {},
  invitableRoles: [],
  editableRoles: [],
  coworkersModalIsOpen: false,
  colleagues: [],
  authUserCanSeeProposal: false,
  review: {},
  showHourlyRate: false,
  showHireAgain: false,
  acceptedProposal: null,
  tabSelected: '2',
  cloraEmployer: false,
  userData: {},
  nextStepsData: {},
  showProjectEmploymentType: false,
  showScreeningQuestions: false,
  googleOmniauthUrl: null,
  msOmniauthUrl: null,
  inviterId: null,
  searchUrl: '',
  cloraRepName: 'Clora Rep',
};

export default ProjectNav;
